import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import ReviewsSection from '../components/ReviewsSection';
import { withPublicUrl } from '../utils/publicPath';

// ── Data ──────────────────────────────────────────────────────────────────────

const qualityItems = [
  {
    num: '01',
    title: 'Maximale Transparenz',
    desc: 'Klare Prozesse und offene Kommunikation. Von der Diagnose bis zur Rechnung wissen Sie immer genau, was an Ihrem Fahrzeug gemacht wird.',
  },
  {
    num: '02',
    title: 'Aluminium & Mischbau',
    desc: 'Langjährige Expertise in der Bearbeitung moderner Karosseriestrukturen. Spezialisiert auf anspruchsvolle Materialien und Fügetechniken.',
  },
  {
    num: '03',
    title: 'Sicherheit & Originalteile',
    desc: 'Ausschließlich Originalteile und herstellerkonforme Reparaturmethoden für Ihre Sicherheit und die Langlebigkeit des Fahrzeugs.',
  },
  {
    num: '04',
    title: 'Garantierte Qualität',
    desc: 'Höchste fachliche Kompetenz gepaart mit modernster Werkstattausrüstung sorgt für Ergebnisse, die dem Originalzustand entsprechen.',
  },
  {
    num: '05',
    title: 'Modernste Ausstattung',
    desc: 'Investition in neueste Werkzeuge und Technologien, um auch komplexeste Unfallschäden präzise und effizient zu beheben.',
  },
  {
    num: '06',
    title: 'Alles aus einer Hand',
    desc: 'Karosserie, Lack und Mechanik unter einem Dach — das sichert schnelle Durchlaufzeiten und lückenlose Qualitätskontrolle.',
  },
];

// ── Animation variants ────────────────────────────────────────────────────────

const qualityGridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const qualityItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

// ── Home ──────────────────────────────────────────────────────────────────────

const Home: React.FC = () => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [videoVisible, setVideoVisible] = React.useState(true);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // On Low Power Mode iOS, play() rejects — hide the video so the hero
    // shows a clean dark background instead of the iOS play button overlay.
    video.play().catch(() => setVideoVisible(false));
  }, []);

  return (
    <PageTransition>
      <div className="bg-black min-vh-100">

        {/* ── Hero — DO NOT TOUCH ──────────────────────────────────── */}
        <section className="hero-section">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', zIndex: 0,
              opacity: videoVisible ? 1 : 0,
            }}
          >
            <source src={withPublicUrl('/videos/231.webm')} type="video/webm" />
          </video>
          <div
            className="hero-overlay"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3))', zIndex: 1 }}
          />
          <div style={{ position: 'relative', zIndex: 2, width: '100%' }}>
            <div className="container">
              <div className="row min-vh-100 align-items-center justify-content-center">
                <div className="col text-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
                    className="hero-logo-container"
                  >
                    <img
                      src={withPublicUrl('/images/logos/logo.png')}
                      alt="CarTech Logo"
                      className="hero-logo"
                      style={{ maxWidth: '600px', width: '100%', height: 'auto', filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5))', display: 'block', margin: '0 auto 1.5rem auto' }}
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="hero-content-below-logo"
                  >
                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                      <Link to="/appointment" style={{ textDecoration: 'none' }}>
                        <button className="btn btn-primary btn-lg px-4 py-2" style={{ backgroundColor: '#FC5000', borderColor: '#FC5000', fontWeight: '600' }}>
                          Termin vereinbaren
                        </button>
                      </Link>
                      <Link to="/leistungen" style={{ textDecoration: 'none' }}>
                        <button className="btn btn-outline-light btn-lg px-4 py-2" style={{ fontWeight: '600' }}>
                          Unsere Leistungen
                        </button>
                      </Link>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 1: Experten Split ────────────────────────────── */}
        <div className="experten-split">

          {/* Left — copy */}
          <motion.div
            className="experten-left"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p style={{ fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-brand)', marginBottom: '0.5rem' }}>
              SEIT 2016
            </p>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 600, color: 'var(--color-text)', marginBottom: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Ihre Experten <span style={{ color: 'var(--color-brand)' }}>seit 2016</span>
            </h2>
            <p style={{ fontSize: '1rem', lineHeight: 1.75, color: 'var(--color-text-secondary)', maxWidth: '480px', marginBottom: 0 }}>
              Wir haben uns vom klassischen Handwerksbetrieb zu einem modernen Dienstleister für komplexe Unfallreparaturen entwickelt. Unsere Stärke liegt in der Kombination von Karosserie, Lack und Mechanik — für schnelle Prozesse und maximale Transparenz.
            </p>
          </motion.div>

          {/* Right — Tesla badge */}
          <motion.div
            className="experten-right"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          >
            <img
              src={withPublicUrl('/images/Tesla-Approved-Body-Shop.png')}
              alt="Tesla Approved Body Shop"
              style={{ width: '200px', maxWidth: '100%', display: 'block', margin: '0 auto' }}
            />
            <div style={{ width: '120px', height: '1px', background: 'rgba(255,255,255,0.15)', margin: '1.5rem auto' }} />
            <p style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>
              Approved Body Shop
            </p>
            <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', maxWidth: '260px', lineHeight: 1.6, marginBottom: 0 }}>
              Zertifiziert für die professionelle Reparatur von Tesla-Fahrzeugen mit Originalteilen.
            </p>
          </motion.div>

        </div>

        {/* ── Section 2: Quality List ──────────────────────────────── */}
        <section className="quality-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <p style={{ fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-brand)', marginBottom: '0.5rem' }}>
              UNSERE QUALITÄTSVERSPRECHEN
            </p>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 600, color: 'var(--color-text)', marginBottom: '1rem', letterSpacing: '-0.02em' }}>
              Warum CarTech Dornbirn?
            </h2>
            <div style={{ width: '40px', height: '3px', background: 'var(--color-brand)', borderRadius: '2px', margin: '0 auto' }} />
          </motion.div>

          <motion.div
            className="quality-grid"
            variants={qualityGridVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {qualityItems.map(item => (
              <motion.div key={item.num} variants={qualityItemVariants}>
                <p style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--color-brand)', opacity: 0.25, lineHeight: 1, marginBottom: '0.25rem' }}>
                  {item.num}
                </p>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.5rem', marginTop: '-0.25rem' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--color-text-secondary)', marginBottom: 0 }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* ── Section 3: CTA Banner ────────────────────────────────── */}
        <section className="cta-banner">
          <div className="cta-banner-inner">
            <div>
              <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 600, color: 'white', marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
                Ihr Fahrzeug in besten Händen
              </h2>
              <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', marginBottom: 0 }}>
                Vereinbaren Sie noch heute einen Termin für Service oder Reparatur.
              </p>
            </div>
            <Link to="/appointment" className="cta-pill-btn">
              Jetzt Termin buchen
            </Link>
          </div>
        </section>

        {/* ── Section 4: Reviews ───────────────────────────────── */}
        <ReviewsSection />

      </div>
    </PageTransition>
  );
};

export default Home;
